import { useState, type FC } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Translate, useTranslation } from "@privacy/translations";
import { Box } from "@mui/material";
import {
  createSystemInformationTypeConnectionMutationOptions,
  deleteSystemInformationTypeConnectionMutationOptions,
  type ConnectedInformationType,
  type ConnectionType,
} from "../../../api/systems";
import { informationTypeSelectOptionsQueryOptions } from "../../../api/informationTypes";
import { InformationTypeIcon2 } from "../../../shared/icons";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { ConfirmDeleteDialog } from "../../../shared/ConfirmDeleteDialog";
import { Drawer } from "../../../shared/Drawer";
import { ConnectionCriticality } from "../../../classification/ConnectionCriticality";
import { usePermission } from "../../../shared/permissions";

type RemoveDialogState =
  { isOpen: true; connection: ConnectedInformationType } | { isOpen: false; connection?: ConnectedInformationType };

const INFORMATION_TYPE_ROW_ICON = <InformationTypeIcon2 size="sm" />;

type SystemConnectionsProps = {
  systemId: string;
  connectionType: ConnectionType;
  connections: Array<ConnectedInformationType>;
  canEdit: boolean;
};

export const SystemConnections: FC<SystemConnectionsProps> = ({ systemId, connectionType, connections, canEdit }) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const { hasPermission } = usePermission();
  const createConnection = useMutation(createSystemInformationTypeConnectionMutationOptions());
  const deleteConnection = useMutation(deleteSystemInformationTypeConnectionMutationOptions());

  const [enableOptionsQuery, setEnableOptionsQuery] = useState(false);
  const informationTypeOptionsQuery = useQuery({
    ...informationTypeSelectOptionsQueryOptions(),
    enabled: enableOptionsQuery,
  });

  const [removeDialog, setRemoveDialog] = useState<RemoveDialogState>({ isOpen: false });

  return (
    <>
      <Drawer.Connections>
        {[...connections]
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .map(connection => (
            <Drawer.Connections.Item
              key={connection.connectionId}
              icon={INFORMATION_TYPE_ROW_ICON}
              name={connection.name}
              onNavigate={() => navigate(paths.informationTypesInline(connection.informationTypeId))}
              disableNavigation={!hasPermission("nova-forms-api:information-type:read")}
              endSlot={
                <>
                  {canEdit && (
                    <Drawer.Connections.RemoveButton
                      onRemove={() => setRemoveDialog({ isOpen: true, connection })}
                      ariaLabel={t("System.Connection.Remove.Tooltip")}
                    />
                  )}
                  {connection.classification?.criticality && (
                    <ConnectionCriticality value={connection.classification.criticality.value} />
                  )}
                </>
              }
            />
          ))}
        {connections.length === 0 && !createConnection.isPending ? (
          <Drawer.Connections.EmptyState
            title={t(
              connectionType === "stored"
                ? "System.Connection.StoredIn.EmptyState.Title"
                : "System.Connection.UsedIn.EmptyState.Title"
            )}
            body={t(
              connectionType === "stored"
                ? "System.Connection.StoredIn.EmptyState.Body"
                : "System.Connection.UsedIn.EmptyState.Body"
            )}
          />
        ) : createConnection.isPending &&
          !connections.some(c => c.informationTypeId === createConnection.variables.informationTypeId) ? (
          <Drawer.Connections.OptimisticItem
            icon={INFORMATION_TYPE_ROW_ICON}
            name={createConnection.variables.informationTypeName}
            onNavigate={() => navigate(paths.informationTypesInline(createConnection.variables.informationTypeId))}
            disableNavigation={!hasPermission("nova-forms-api:information-type:read")}
          />
        ) : null}
        {canEdit && (
          <Drawer.Connections.Add
            options={informationTypeOptionsQuery.data}
            excludeIds={[
              ...connections.map(c => c.informationTypeId),
              ...(createConnection.isPending ? [createConnection.variables.informationTypeId] : []),
            ]}
            onMouseEnter={() => setEnableOptionsQuery(true)}
            onFocus={() => setEnableOptionsQuery(true)}
            onAdd={option =>
              createConnection.mutateAsync({
                systemId,
                informationTypeId: option.id,
                informationTypeName: option.name,
                connectionType,
              })
            }
            searchPlaceholder={t("System.Connection.Picker.SearchPlaceholder")}
          />
        )}
      </Drawer.Connections>
      {removeDialog.isOpen && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setRemoveDialog({ isOpen: false })}
          onDelete={() =>
            deleteConnection.mutateAsync({ systemId, connectionId: removeDialog.connection.connectionId })
          }
          headerText={t("System.Connection.Remove.ConfirmHeader")}
          message={
            <Translate
              translationKey="System.Connection.Remove.ConfirmMessage"
              values={{
                name: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {removeDialog.connection.name}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t("System.Connection.Remove.ConfirmButton")}
        />
      )}
    </>
  );
};
